/* eslint-disable react/prop-types */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Button, Typography, LinearProgress, CircularProgress,
  Chip, Divider, Checkbox, FormControlLabel,
  IconButton, Alert, Paper,
  Accordion, AccordionSummary, AccordionDetails, TextField,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Psychology as AIIcon,
  DocumentScanner as ScannerIcon,
  AutoFixHigh as AutoFillIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  EventAvailable as FollowUpIcon,
  TextSnippet as SynopsisIcon,
  CloudDone as CloudDoneIcon,
  AutoFixNormal as CleanIcon,
  Biotech as BiotechIcon,
  PersonOutline as DoctorIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from 'sweetalert2';
import { api } from '../../api';

// Minimal quill toolbar for medical notes
const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet'];

const isQuillEmpty = (val) => !val || val === '<p><br></p>' || val.replace(/<[^>]*>/g, '').trim() === '';

// ── Constants ──────────────────────────────────────────────────────────────────
const TEAL = '#00798E';
const TEAL_LIGHT = '#00b4cc';
const TEAL_DARK = '#005f70';
const WARN_BORDER = '#ffc107';
const LOW_CONF_COLOR = '#fffde7';
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, 'application/pdf'];

const STATUS_LABELS = {
  PENDING:       'Queued for processing…',
  UPLOADING:     'Uploading to cloud…',
  OCR_RUNNING:   'Reading prescription via OCR…',
  CLEANING_TEXT: 'Cleaning extracted text…',
  AI_PROCESSING: 'AI extracting clinical data…',
  COMPLETED:     'Extraction complete!',
  FAILED:        'Processing failed',
  RETRYING:      'Retrying…',
};

const STAGE_PROGRESS = {
  PENDING: 5, UPLOADING: 22, RETRYING: 28,
  OCR_RUNNING: 50, CLEANING_TEXT: 72, AI_PROCESSING: 88,
  COMPLETED: 100, FAILED: 100,
};

const STATUS_ORDER = [
  'PENDING', 'UPLOADING', 'RETRYING',
  'OCR_RUNNING', 'CLEANING_TEXT', 'AI_PROCESSING',
  'COMPLETED', 'FAILED',
];

const STEPS = [
  { key: 'UPLOADING',     label: 'Upload',     Icon: CloudDoneIcon },
  { key: 'OCR_RUNNING',   label: 'OCR Read',   Icon: ScannerIcon },
  { key: 'CLEANING_TEXT', label: 'Clean',      Icon: CleanIcon },
  { key: 'AI_PROCESSING', label: 'AI Extract', Icon: BiotechIcon },
  { key: 'COMPLETED',     label: 'Done',       Icon: CheckIcon },
];

// ── Sub-components ─────────────────────────────────────────────────────────────
const fmtConf = (c) => c !== null && c !== undefined ? `${Math.round(c * 100)}%` : '—';

const UNKNOWN_TOKENS = new Set(['unknown', '(unknown)', '(unknown name)', 'null', 'undefined', 'n/a', 'na', 'none', '-', '—']);
const isUnknown = (val) => {
  if (val === null || val === undefined || val === '') return true;
  return UNKNOWN_TOKENS.has(String(val).trim().toLowerCase());
};

function ConfBadge({ confidence, threshold }) {
  if (confidence === null || confidence === undefined) return null;
  const low = confidence < threshold;
  return (
    <Chip
      size="small"
      label={fmtConf(confidence)}
      icon={low ? <WarningIcon style={{ fontSize: 11 }} /> : <CheckIcon style={{ fontSize: 11 }} />}
      sx={{
        height: 18, fontSize: '0.63rem', fontWeight: 700, ml: 0.5,
        backgroundColor: low ? WARN_BORDER : '#d4edda',
        color: low ? '#856404' : '#155724',
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );
}

function FieldRow({ label, value, confidence, threshold }) {
  if (isUnknown(value)) return null;
  const low = confidence !== null && confidence !== undefined && confidence < threshold;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1,
      py: 0.5, px: 1, borderRadius: 1, mb: 0.5,
      backgroundColor: low ? LOW_CONF_COLOR : 'transparent',
      border: low ? `1px solid ${WARN_BORDER}` : '1px solid transparent',
    }}>
      <Typography variant="caption" sx={{ minWidth: 116, color: '#666', fontWeight: 600, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ flex: 1, wordBreak: 'break-word', color: '#222' }}>
        {value}
      </Typography>
      <ConfBadge confidence={confidence} threshold={threshold} />
    </Box>
  );
}

function SectionHeader({ children, action }) {
  return (
    <Box sx={{ mt: 1.5, mb: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="caption" sx={{
        fontWeight: 700, color: TEAL, textTransform: 'uppercase',
        letterSpacing: '0.07em', fontSize: '0.68rem',
        borderLeft: `3px solid ${TEAL}`, pl: 0.75,
      }}>
        {children}
      </Typography>
      {action}
    </Box>
  );
}

// ── Main Dialog ────────────────────────────────────────────────────────────────
export default function ScanPrescriptionDialog({
  open, onClose, prescriptionId, prescriptionCode,
  onApplyMedications, onApplyInvestigations, onApplyVitals, onApplyAll,
  onUpdateSynopsis, onCreateFollowUp,
}) {
  // File state (multi-file: 1 PDF or up to 3 images)
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Job state
  const [jobRef, setJobRef] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);

  // Results selection
  const [selectedMeds, setSelectedMeds] = useState({});
  const [selectedInvs, setSelectedInvs] = useState({});
  const [applyVitals, setApplyVitals] = useState(true);
  const [applySynopsis, setApplySynopsis] = useState(true);

  // Editable core fields
  const [editDoctor, setEditDoctor] = useState('');
  const [editSynopsis, setEditSynopsis] = useState('');
  const [editDiagFemale, setEditDiagFemale] = useState('');
  const [editDiagMale, setEditDiagMale] = useState('');
  const [editFollowupDate, setEditFollowupDate] = useState('');

  // Action states
  const [synopsisSaving, setSynopsisSaving] = useState(false);
  const [followupSaving, setFollowupSaving] = useState(false);

  // Normalize: ApiClient already returns response.data; after poll jobStatus IS the HTTP body
  // { statusCode, flag, data: { status, ai_result, ... } } → jobData = .data || self
  const jobData = jobStatus?.data ?? jobStatus;
  const threshold = jobData?.confidence_threshold ?? 0.80;
  const aiResult = jobData?.ai_result;
  const isCompleted = jobData?.status === 'COMPLETED';
  const isFailed = jobData?.status === 'FAILED';
  const isProcessing = !!(jobRef && !isCompleted && !isFailed);
  const currentStatus = jobData?.status;
  const progressPercent = STAGE_PROGRESS[currentStatus] ?? 0;

  // Populate edit fields when AI result arrives
  useEffect(() => {
    if (!aiResult) return;
    const meds = aiResult.medications || [];
    const invs = aiResult.investigations || [];
    setSelectedMeds(Object.fromEntries(meds.map((_, i) => [i, true])));
    setSelectedInvs(Object.fromEntries(invs.map((_, i) => [i, true])));

    const core = aiResult.core || {};
    setEditDoctor(aiResult.doctorName?.value || '');
    setEditSynopsis(aiResult.prescriptionSynopsis?.value || core.synopsis || '');
    setEditDiagFemale(core.probable_diagnosis_female || aiResult.probableDiagnosisFemale?.value || '');
    setEditDiagMale(core.probable_diagnosis_male || aiResult.probableDiagnosisMale?.value || '');
    setEditFollowupDate(core.followup_date || aiResult.followUp?.value || '');
  }, [aiResult]);

  useEffect(() => {
    if (!open) clearInterval(pollTimerRef.current);
    return () => clearInterval(pollTimerRef.current);
  }, [open]);

  const resetState = () => {
    setFiles([]);
    setJobRef(null);
    setJobStatus(null);
    setPolling(false);
    setUploadError(null);
    setSelectedMeds({});
    setSelectedInvs({});
    setApplyVitals(true);
    setApplySynopsis(true);
    setEditDoctor('');
    setEditSynopsis('');
    setEditDiagFemale('');
    setEditDiagMale('');
    setEditFollowupDate('');
    setSynopsisSaving(false);
    setFollowupSaving(false);
    clearInterval(pollTimerRef.current);
    pollAttemptsRef.current = 0;
  };

  // ── File handling ─────────────────────────────────────────────────────────
  const handleFileSelect = useCallback((newFiles) => {
    const valid = Array.from(newFiles).filter(f => ALLOWED_TYPES.includes(f.type));
    if (valid.length === 0) {
      setUploadError('Unsupported file type. Allowed: JPG, PNG, WEBP, PDF.');
      return;
    }
    setUploadError(null);

    const hasPdf = valid.some(f => f.type === 'application/pdf');
    if (hasPdf) {
      if (valid.length > 1) {
        setUploadError('Only 1 PDF file at a time. Extra files ignored.');
      }
      setFiles([valid[0]]);
      return;
    }

    // Images
    const existingHasPdf = files.some(f => f.type === 'application/pdf');
    if (existingHasPdf) {
      setUploadError('Remove the PDF before adding images.');
      return;
    }
    const combined = [...files, ...valid].slice(0, 3);
    if (files.length + valid.length > 3) {
      setUploadError('Maximum 3 images. Extra files were ignored.');
    }
    setFiles(combined);
  }, [files]);

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setUploadError(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  // ── Upload & poll ─────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploadError(null);
    try {
      const res = await api.opd.uploadPrescriptionAiScan(files, prescriptionId || null);
      const ref = res?.data?.job_ref || res?.job_ref;
      if (!ref) throw new Error('No job reference returned from server');
      setJobRef(ref);
      setJobStatus({ status: 'PENDING' });
      startPolling(ref);
    } catch (err) {
      setUploadError(err?.response?.data?.message || err?.message || 'Upload failed');
    }
  };

  const startPolling = (ref) => {
    setPolling(true);
    pollAttemptsRef.current = 0;
    const poll = async () => {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        clearInterval(pollTimerRef.current);
        setPolling(false);
        setUploadError('Processing is taking too long. Please try again later.');
        return;
      }
      try {
        const res = await api.opd.getPrescriptionAiScanStatus(ref);
        setJobStatus(res?.data || res);
        const status = res?.data?.data?.status || res?.data?.status;
        if (status === 'COMPLETED' || status === 'FAILED') {
          clearInterval(pollTimerRef.current);
          setPolling(false);
          if (status === 'COMPLETED' && prescriptionId) {
            api.opd.linkAiScanToPrescription(ref, prescriptionId).catch(() => {});
          }
        }
      } catch { /* keep polling on network hiccup */ }
    };
    poll();
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
  };

  // ── Apply to prescription form ────────────────────────────────────────────
  const handleApply = () => {
    if (!aiResult) return;
    const meds = (aiResult.medications || []).filter((_, i) => selectedMeds[i]).map(m => ({
      name: m.name?.value || '',
      dosage: [m.dosage?.value, m.strength?.value].filter(Boolean).join(' ') || '',
      frequency: m.frequency?.value || '',
      duration: m.duration?.value || '',
      instructions: m.instructions?.value || '',
      route: m.route?.value || '',
      quantity: 1,
    }));
    const invs = (aiResult.investigations || []).filter((_, i) => selectedInvs[i]).map(inv => ({
      name: inv.name?.value || '',
      description: inv.description?.value || '',
      instructions: inv.instructions?.value || '',
      priority: inv.priority?.value || 'NORMAL',
      status: 'PENDING',
    }));
    const v = aiResult.vitals || {};
    const bpParts = (v.bp?.value || '').split('/');
    const vitals = {
      weight: v.weight?.value || null,
      height: v.height?.value || null,
      bp_systolic: bpParts.length === 2 ? parseInt(bpParts[0], 10) || null : null,
      bp_diastolic: bpParts.length === 2 ? parseInt(bpParts[1], 10) || null : null,
      synopsis: applySynopsis ? (editSynopsis || null) : null,
      probable_diagonosis_female: applySynopsis ? (isQuillEmpty(editDiagFemale) ? null : editDiagFemale) : null,
      probable_diagonosis_male: applySynopsis ? (isQuillEmpty(editDiagMale) ? null : editDiagMale) : null,
      remarks: aiResult.advice?.value || null,
      visitDate: aiResult.visitDate?.value || null,
    };
    if (onApplyAll) { onApplyAll({ meds, invs, vitals: applyVitals ? vitals : null, jobRef }); onClose(); return; }
    if (meds.length > 0 && onApplyMedications) onApplyMedications(meds);
    if (invs.length > 0 && onApplyInvestigations) onApplyInvestigations(invs);
    if (applyVitals && onApplyVitals) onApplyVitals(vitals);
    onClose();
  };

  // ── Save synopsis / diagnosis ─────────────────────────────────────────────
  const handleUpdateSynopsis = async () => {
    if (!onUpdateSynopsis) return;
    setSynopsisSaving(true);
    try {
      await onUpdateSynopsis({
        synopsis: isQuillEmpty(editSynopsis) ? null : editSynopsis,
        probable_diagnosis_female: isQuillEmpty(editDiagFemale) ? null : editDiagFemale,
        probable_diagnosis_male: isQuillEmpty(editDiagMale) ? null : editDiagMale,
      });
      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: 'Synopsis & diagnosis saved to prescription successfully.',
        timer: 2500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: e?.response?.data?.message || e?.message || 'Failed to save synopsis. Please try again.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK',
      });
    } finally {
      setSynopsisSaving(false);
    }
  };

  // ── Create follow-up ──────────────────────────────────────────────────────
  const handleCreateFollowUp = async () => {
    if (!onCreateFollowUp) return;
    if (!editFollowupDate) {
      Swal.fire({ icon: 'warning', title: 'Date Required', text: 'Please select a follow-up date first.', confirmButtonColor: '#00798E', confirmButtonText: 'OK' });
      return;
    }
    setFollowupSaving(true);
    try {
      await onCreateFollowUp({ followupDate: editFollowupDate });
      Swal.fire({
        icon: 'success',
        title: 'Follow-Up Created!',
        text: `Follow-up scheduled for ${dayjs(editFollowupDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}.`,
        timer: 2500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Failed',
        text: e?.response?.data?.message || e?.message || 'Failed to create follow-up. Please try again.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK',
      });
    } finally {
      setFollowupSaving(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Multi-file upload zone
  // ────────────────────────────────────────────────────────────────────────────
  const hasPdf = files.some(f => f.type === 'application/pdf');
  const canAddMore = !hasPdf && files.length < 3;

  const renderUploadZone = () => (
    <Box>
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => files.length === 0 && fileInputRef.current?.click()}
        sx={{
          border: `2px dashed ${dragOver ? TEAL : files.length > 0 ? 'rgba(0,121,142,0.3)' : '#d0d0d0'}`,
          borderRadius: 2.5, py: files.length > 0 ? 2 : 5, px: 3,
          textAlign: 'center', transition: 'all 0.2s ease',
          cursor: files.length === 0 ? 'pointer' : 'default',
          backgroundColor: dragOver
            ? 'rgba(0,121,142,0.06)'
            : files.length > 0 ? 'rgba(0,121,142,0.025)' : '#fafafa',
          '&:hover': files.length === 0 ? { borderColor: TEAL, backgroundColor: 'rgba(0,121,142,0.04)' } : {},
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.length) handleFileSelect(e.target.files); e.target.value = ''; }}
        />

        {files.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: dragOver ? `linear-gradient(135deg, ${TEAL_DARK}, ${TEAL_LIGHT})` : 'rgba(0,121,142,0.08)',
              transition: 'all 0.3s ease',
            }}>
              <UploadIcon sx={{ fontSize: 30, color: dragOver ? '#fff' : TEAL }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700} color={dragOver ? TEAL : '#444'} sx={{ mb: 0.25 }}>
                Drag & drop prescriptions here
              </Typography>
              <Typography variant="caption" color="#999">
                1 PDF  <strong style={{ color: '#666' }}>or</strong>  up to 3 images (JPG, PNG, WEBP) — max 20 MB each
              </Typography>
            </Box>
            <Button
              variant="outlined" size="small" startIcon={<UploadIcon sx={{ fontSize: 15 }} />}
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              sx={{ mt: 0.5, borderColor: TEAL, color: TEAL, fontSize: '0.75rem', borderRadius: 2, px: 2, '&:hover': { borderColor: TEAL_DARK, backgroundColor: 'rgba(0,121,142,0.06)' } }}
            >
              Browse Files
            </Button>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: canAddMore ? 1.5 : 0 }}>
              {files.map((f, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 1.25, borderRadius: 1.5, textAlign: 'left',
                  backgroundColor: '#fff', border: `1px solid rgba(0,121,142,0.2)`,
                }}>
                  {f.type === 'application/pdf'
                    ? <PdfIcon sx={{ color: '#e53935', fontSize: 28, flexShrink: 0 }} />
                    : <ImageIcon sx={{ color: TEAL, fontSize: 28, flexShrink: 0 }} />
                  }
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.82rem' }}>{f.name}</Typography>
                    <Typography variant="caption" color="#888">
                      {(f.size / 1024 / 1024).toFixed(2)} MB · {f.type.split('/')[1]?.toUpperCase()}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeFile(i); }} sx={{ color: '#bbb', '&:hover': { color: '#e53935', backgroundColor: 'rgba(229,57,53,0.08)' } }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
            {canAddMore && (
              <Box
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                  p: 1, borderRadius: 1.5, cursor: 'pointer',
                  border: `1.5px dashed rgba(0,121,142,0.35)`,
                  color: TEAL, fontSize: '0.78rem', fontWeight: 600,
                  '&:hover': { backgroundColor: 'rgba(0,121,142,0.05)', borderColor: TEAL },
                  transition: 'all 0.15s ease',
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add more images ({3 - files.length} slot{3 - files.length !== 1 ? 's' : ''} remaining)
              </Box>
            )}
            {hasPdf && (
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.75 }}>
                PDF selected — single file only
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {uploadError && (
        <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.8rem', borderRadius: 2 }}>{uploadError}</Alert>
      )}
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Animated processing stepper
  // ────────────────────────────────────────────────────────────────────────────
  const renderAnimatedStepper = () => {
    const currentIdx = STATUS_ORDER.indexOf(currentStatus);
    return (
      <Box>
        {/* Status text + percentage */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.83rem', color: isFailed ? '#c62828' : TEAL_DARK }}>
            {STATUS_LABELS[currentStatus] || 'Processing…'}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.78rem', color: isFailed ? '#c62828' : TEAL }}>
            {progressPercent}%
          </Typography>
        </Box>

        {/* Gradient progress bar with shimmer */}
        <Box sx={{ position: 'relative', mb: 3.5 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 10, borderRadius: 5,
              backgroundColor: 'rgba(0,121,142,0.1)',
              '& .MuiLinearProgress-bar': {
                background: isFailed
                  ? 'linear-gradient(90deg, #ef5350 0%, #e53935 100%)'
                  : `linear-gradient(90deg, ${TEAL_DARK} 0%, ${TEAL} 55%, ${TEAL_LIGHT} 100%)`,
                borderRadius: 5,
                transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
                boxShadow: isProcessing && !isFailed ? `0 0 10px ${TEAL_LIGHT}` : 'none',
              },
            }}
          />
          {isProcessing && !isFailed && (
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              borderRadius: 5, overflow: 'hidden', pointerEvents: 'none',
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
              animation: 'shimmer 1.8s ease-in-out infinite',
              '@keyframes shimmer': { '0%': { transform: 'translateX(-100%)' }, '100%': { transform: 'translateX(100%)' } },
            }} />
          )}
        </Box>

        {/* Step indicators with connecting lines */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          {STEPS.map((step, i) => {
            const stepIdx = STATUS_ORDER.indexOf(step.key);
            const isDone = (currentIdx > stepIdx && !isFailed) || (step.key === 'COMPLETED' && isCompleted);
            const isActive = currentStatus === step.key
              || (currentStatus === 'RETRYING' && step.key === 'OCR_RUNNING')
              || (currentStatus === 'PENDING' && step.key === 'UPLOADING');
            const { Icon: StepIcon } = step;

            return (
              <React.Fragment key={step.key}>
                {i > 0 && (
                  <Box sx={{
                    flex: 1, height: 2, mt: '14px',
                    borderRadius: 1,
                    background: isDone
                      ? `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%)`
                      : 'rgba(0,0,0,0.07)',
                    transition: 'background 0.6s ease',
                  }} />
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isDone
                      ? `linear-gradient(135deg, ${TEAL_DARK} 0%, ${TEAL_LIGHT} 100%)`
                      : isActive ? 'rgba(0,121,142,0.1)' : 'rgba(0,0,0,0.04)',
                    border: `2px solid ${isDone ? 'transparent' : isActive ? TEAL : 'rgba(0,0,0,0.1)'}`,
                    color: isDone ? '#fff' : isActive ? TEAL : '#ccc',
                    transition: 'all 0.4s ease',
                    animation: isActive ? 'stepGlow 1.6s ease-in-out infinite' : 'none',
                    '@keyframes stepGlow': {
                      '0%,100%': { boxShadow: 'none' },
                      '50%': { boxShadow: `0 0 0 5px rgba(0,121,142,0.18), 0 0 12px rgba(0,180,204,0.3)` },
                    },
                  }}>
                    {isDone
                      ? <CheckIcon sx={{ fontSize: 15 }} />
                      : <StepIcon sx={{ fontSize: 15 }} />
                    }
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: '0.6rem', lineHeight: 1.2, textAlign: 'center',
                    fontWeight: isDone || isActive ? 700 : 400,
                    color: isDone ? TEAL_DARK : isActive ? TEAL : '#ccc',
                    whiteSpace: 'nowrap',
                  }}>
                    {step.label}
                  </Typography>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>

        {isFailed && (
          <Alert severity="error" sx={{ mt: 2.5, fontSize: '0.8rem', borderRadius: 2 }} icon={<ErrorIcon />}>
            <strong>Processing failed.</strong>
            {jobData?.processing_error && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
                {jobData.processing_error}
              </Typography>
            )}
          </Alert>
        )}
      </Box>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Synopsis & follow-up editable section (primary results area)
  // ────────────────────────────────────────────────────────────────────────────
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.83rem',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL, borderWidth: 2 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: TEAL },
  };

  // Shared style for quill editor wrappers to look like MUI OutlinedInput
  const quillWrapSx = {
    border: '1px solid rgba(0,0,0,0.23)',
    borderRadius: '4px',
    transition: 'border-color 0.2s',
    '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
    '&:focus-within': { borderColor: TEAL, borderWidth: '2px' },
    '& .ql-toolbar.ql-snow': {
      borderBottom: '1px solid rgba(0,0,0,0.1)',
      borderTop: 'none', borderLeft: 'none', borderRight: 'none',
      padding: '3px 8px',
      '& .ql-formats': { marginRight: 8 },
      '& button': { width: 24, height: 24 },
    },
    '& .ql-container.ql-snow': {
      border: 'none',
      fontSize: '0.83rem',
      fontFamily: 'inherit',
    },
    '& .ql-editor': {
      minHeight: '72px',
      maxHeight: '140px',
      overflowY: 'auto',
      padding: '8px 12px',
      lineHeight: 1.6,
    },
    '& .ql-editor.ql-blank::before': {
      fontStyle: 'italic',
      color: 'rgba(0,0,0,0.38)',
      fontSize: '0.83rem',
    },
  };

  const QuillLabel = ({ children }) => (
    <Typography variant="caption" sx={{
      display: 'block', mb: 0.5,
      color: 'rgba(0,0,0,0.6)', fontSize: '0.75rem',
      fontFamily: 'inherit',
    }}>
      {children}
    </Typography>
  );

  const renderSynopsisSection = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" icon={<SynopsisIcon sx={{ color: TEAL, fontSize: 18 }} />}
        sx={{ fontSize: '0.78rem', py: 0.5, backgroundColor: 'rgba(0,121,142,0.06)', color: TEAL_DARK, border: `1px solid rgba(0,121,142,0.18)`, borderRadius: 2 }}>
        AI has pre-filled the fields from the prescription. Edit as needed, then save.
      </Alert>

      {/* Doctor — auto-populated from AI extraction (read-only) */}
      <TextField
        label="Doctor (from prescription)"
        value={editDoctor}
        InputProps={{
          readOnly: true,
          startAdornment: <DoctorIcon sx={{ fontSize: 18, color: TEAL, mr: 0.75 }} />,
        }}
        size="small"
        variant="outlined"
        placeholder="Doctor name will appear when extracted by AI"
        sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { fontSize: '0.83rem', backgroundColor: 'rgba(0,0,0,0.02)' } }}
      />

      {/* Diagnosis fields — rich text editors side by side */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <QuillLabel>Probable Diagnosis (Female)</QuillLabel>
          <Box sx={quillWrapSx}>
            <ReactQuill
              theme="snow"
              value={editDiagFemale}
              onChange={setEditDiagFemale}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Female diagnosis…"
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuillLabel>Probable Diagnosis (Male)</QuillLabel>
          <Box sx={quillWrapSx}>
            <ReactQuill
              theme="snow"
              value={editDiagMale}
              onChange={setEditDiagMale}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Male diagnosis…"
            />
          </Box>
        </Box>
      </Box>

      {/* Synopsis — rich text editor */}
      <Box>
        <QuillLabel>Prescription Synopsis — verbatim doctor&apos;s note</QuillLabel>
        <Box sx={{
          ...quillWrapSx,
          '& .ql-editor': {
            minHeight: '160px',
            maxHeight: '260px',
            overflowY: 'auto',
            padding: '10px 12px',
            lineHeight: 1.7,
          },
        }}>
          <ReactQuill
            theme="snow"
            value={editSynopsis}
            onChange={setEditSynopsis}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="The complete prescription text as written by the doctor will appear here…"
          />
        </Box>
      </Box>

      {/* Follow-up date — MUI DatePicker DD/MM/YYYY */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Follow-up Date"
            value={editFollowupDate ? dayjs(editFollowupDate, 'YYYY-MM-DD') : null}
            onChange={(newVal) => setEditFollowupDate(newVal ? newVal.format('YYYY-MM-DD') : '')}
            inputFormat="DD/MM/YYYY"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                sx={{ ...fieldSx, width: 210 }}
              />
            )}
          />
        </LocalizationProvider>
        {editFollowupDate && (
          <Typography variant="caption" sx={{ color: '#888' }}>
            Follow-up on {dayjs(editFollowupDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
          </Typography>
        )}
      </Box>

    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Patient info & vitals (for accordion)
  // ────────────────────────────────────────────────────────────────────────────
  const renderPatientInfo = () => {
    if (!aiResult) return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <CircularProgress size={22} sx={{ color: TEAL }} />
      </Box>
    );
    return (
      <Box>
        <SectionHeader>Patient & Visit</SectionHeader>
        <FieldRow label="Patient Name" value={aiResult.patientName?.value} confidence={aiResult.patientName?.confidence} threshold={threshold} />
        <FieldRow label="Age" value={aiResult.age?.value} confidence={aiResult.age?.confidence} threshold={threshold} />
        <FieldRow label="Gender" value={aiResult.gender?.value} confidence={aiResult.gender?.confidence} threshold={threshold} />
        <FieldRow label="Visit Date" value={aiResult.visitDate?.value} confidence={aiResult.visitDate?.confidence} threshold={threshold} />
        <FieldRow label="Doctor" value={aiResult.doctorName?.value} confidence={aiResult.doctorName?.confidence} threshold={threshold} />
        <FieldRow label="Department" value={aiResult.department?.value} confidence={aiResult.department?.confidence} threshold={threshold} />

        <SectionHeader>Clinical</SectionHeader>
        <FieldRow label="Chief Complaint" value={aiResult.chiefComplaint?.value} confidence={aiResult.chiefComplaint?.confidence} threshold={threshold} />
        <FieldRow label="Diagnosis" value={aiResult.diagnosis?.value} confidence={aiResult.diagnosis?.confidence} threshold={threshold} />
        <FieldRow label="Clinical Notes" value={aiResult.clinicalNotes?.value} confidence={aiResult.clinicalNotes?.confidence} threshold={threshold} />
        <FieldRow label="Advice" value={aiResult.advice?.value} confidence={aiResult.advice?.confidence} threshold={threshold} />
        <FieldRow label="Follow Up" value={aiResult.followUp?.value} confidence={aiResult.followUp?.confidence} threshold={threshold} />

        <SectionHeader
          action={
            <FormControlLabel
              control={<Checkbox size="small" checked={applyVitals} onChange={e => setApplyVitals(e.target.checked)} sx={{ p: 0.25, '&.Mui-checked': { color: TEAL } }} />}
              label={<Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Apply vitals to form</Typography>}
              sx={{ m: 0 }}
            />
          }
        >
          Vitals
        </SectionHeader>
        <FieldRow label="BP" value={aiResult.vitals?.bp?.value} confidence={aiResult.vitals?.bp?.confidence} threshold={threshold} />
        <FieldRow label="Pulse" value={aiResult.vitals?.pulse?.value} confidence={aiResult.vitals?.pulse?.confidence} threshold={threshold} />
        <FieldRow label="Temperature" value={aiResult.vitals?.temperature?.value} confidence={aiResult.vitals?.temperature?.confidence} threshold={threshold} />
        <FieldRow label="Weight" value={aiResult.vitals?.weight?.value} confidence={aiResult.vitals?.weight?.confidence} threshold={threshold} />
        <FieldRow label="Height" value={aiResult.vitals?.height?.value} confidence={aiResult.vitals?.height?.confidence} threshold={threshold} />

        {aiResult.unknownText?.value && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="#888" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Unclassified Text
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', color: '#555', p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {aiResult.unknownText.value}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Medications (for accordion)
  // ────────────────────────────────────────────────────────────────────────────
  const renderMedications = () => {
    const allMeds = aiResult?.medications || [];
    // Split into selectable (known name) and display-only (unknown name)
    const knownMeds = allMeds.map((med, origIdx) => ({ med, origIdx })).filter(({ med }) => !isUnknown(med.name?.value));
    const unknownMeds = allMeds.filter(med => isUnknown(med.name?.value));

    if (allMeds.length === 0) return (
      <Typography variant="body2" color="#999" sx={{ py: 2, textAlign: 'center' }}>No medications detected</Typography>
    );

    const MedCard = ({ med, origIdx, selectable }) => {
      const lowConf = Object.values(med).some(f => f?.confidence !== null && f?.confidence !== undefined && f.confidence < threshold);
      const dosage = !isUnknown(med.dosage?.value) ? med.dosage : null;
      const frequency = !isUnknown(med.frequency?.value) ? med.frequency : null;
      const duration = !isUnknown(med.duration?.value) ? med.duration : null;
      const instructions = !isUnknown(med.instructions?.value) ? med.instructions?.value : null;
      const strength = !isUnknown(med.strength?.value) ? med.strength?.value : null;
      const route = !isUnknown(med.route?.value) ? med.route?.value : null;
      return (
        <Paper key={origIdx} variant="outlined" sx={{
          mb: 1, p: 1.5, borderRadius: 2,
          backgroundColor: lowConf ? LOW_CONF_COLOR : '#fff',
          borderColor: lowConf ? WARN_BORDER : '#e8e8e8',
          opacity: selectable ? 1 : 0.6,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {selectable && (
              <Checkbox
                size="small"
                checked={!!selectedMeds[origIdx]}
                onChange={e => setSelectedMeds(p => ({ ...p, [origIdx]: e.target.checked }))}
                sx={{ mt: -0.25, '&.Mui-checked': { color: TEAL } }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: selectable ? '#111' : '#888' }}>
                  {med.name?.value}
                </Typography>
                <ConfBadge confidence={med.name?.confidence} threshold={threshold} />
                {strength && <Chip size="small" label={strength} sx={{ height: 18, fontSize: '0.63rem', backgroundColor: '#e3f2fd', color: '#1565c0' }} />}
                {route && <Chip size="small" label={route} sx={{ height: 18, fontSize: '0.63rem' }} />}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {dosage && <FieldRow label="Dosage" value={dosage.value} confidence={dosage.confidence} threshold={threshold} />}
                {frequency && <FieldRow label="Frequency" value={frequency.value} confidence={frequency.confidence} threshold={threshold} />}
                {duration && <FieldRow label="Duration" value={duration.value} confidence={duration.confidence} threshold={threshold} />}
              </Box>
              {instructions && <Typography variant="caption" color="#555" sx={{ mt: 0.25, display: 'block', fontStyle: 'italic' }}>{instructions}</Typography>}
            </Box>
          </Box>
        </Paper>
      );
    };

    return (
      <Box>
        {knownMeds.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="#666">Select medications to apply to form:</Typography>
            <Button size="small" sx={{ fontSize: '0.7rem', color: TEAL }}
              onClick={() => setSelectedMeds(Object.fromEntries(knownMeds.map(({ origIdx }) => [origIdx, true])))}>
              Select All
            </Button>
          </Box>
        )}
        {knownMeds.map(({ med, origIdx }) => (
          <MedCard key={origIdx} med={med} origIdx={origIdx} selectable />
        ))}
        {unknownMeds.length > 0 && knownMeds.length > 0 && (
          <Typography variant="caption" sx={{ display: 'block', color: '#aaa', mt: 1, mb: 0.5, pl: 0.5 }}>
            Additional entries (name unclear)
          </Typography>
        )}
        {unknownMeds.map((med, i) => (
          <MedCard key={`u-${i}`} med={med} origIdx={-1} selectable={false} />
        ))}
      </Box>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Investigations (for accordion)
  // ────────────────────────────────────────────────────────────────────────────
  const renderInvestigations = () => {
    const allInvs = aiResult?.investigations || [];
    const knownInvs = allInvs.map((inv, origIdx) => ({ inv, origIdx })).filter(({ inv }) => !isUnknown(inv.name?.value));
    const unknownInvs = allInvs.filter(inv => isUnknown(inv.name?.value));

    if (allInvs.length === 0) return (
      <Typography variant="body2" color="#999" sx={{ py: 2, textAlign: 'center' }}>No investigations detected</Typography>
    );

    const InvCard = ({ inv, origIdx, selectable }) => {
      const lowConf = Object.values(inv).some(f => f?.confidence !== null && f?.confidence !== undefined && f.confidence < threshold);
      const description = !isUnknown(inv.description?.value) ? inv.description?.value : null;
      const instructions = !isUnknown(inv.instructions?.value) ? inv.instructions?.value : null;
      const priority = !isUnknown(inv.priority?.value) ? inv.priority?.value : null;
      return (
        <Paper key={origIdx} variant="outlined" sx={{
          mb: 1, p: 1.5, borderRadius: 2,
          backgroundColor: lowConf ? LOW_CONF_COLOR : '#fff',
          borderColor: lowConf ? WARN_BORDER : '#e8e8e8',
          opacity: selectable ? 1 : 0.6,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {selectable && (
              <Checkbox
                size="small"
                checked={!!selectedInvs[origIdx]}
                onChange={e => setSelectedInvs(p => ({ ...p, [origIdx]: e.target.checked }))}
                sx={{ mt: -0.25, '&.Mui-checked': { color: TEAL } }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: selectable ? '#111' : '#888' }}>
                  {inv.name?.value}
                </Typography>
                <ConfBadge confidence={inv.name?.confidence} threshold={threshold} />
                {priority && <Chip size="small" label={priority} sx={{ height: 18, fontSize: '0.63rem' }} />}
              </Box>
              {description && <Typography variant="caption" color="#555">{description}</Typography>}
              {instructions && <Typography variant="caption" color="#777" display="block" sx={{ fontStyle: 'italic' }}>{instructions}</Typography>}
            </Box>
          </Box>
        </Paper>
      );
    };

    return (
      <Box>
        {knownInvs.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="#666">Select investigations to apply to form:</Typography>
            <Button size="small" sx={{ fontSize: '0.7rem', color: TEAL }}
              onClick={() => setSelectedInvs(Object.fromEntries(knownInvs.map(({ origIdx }) => [origIdx, true])))}>
              Select All
            </Button>
          </Box>
        )}
        {knownInvs.map(({ inv, origIdx }) => (
          <InvCard key={origIdx} inv={inv} origIdx={origIdx} selectable />
        ))}
        {unknownInvs.length > 0 && knownInvs.length > 0 && (
          <Typography variant="caption" sx={{ display: 'block', color: '#aaa', mt: 1, mb: 0.5, pl: 0.5 }}>
            Additional entries (name unclear)
          </Typography>
        )}
        {unknownInvs.map((inv, i) => (
          <InvCard key={`u-${i}`} inv={inv} origIdx={-1} selectable={false} />
        ))}
      </Box>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Consolidated results view (post-completion)
  // ────────────────────────────────────────────────────────────────────────────
  const medCount = (aiResult?.medications || []).length;
  const invCount = (aiResult?.investigations || []).length;
  const showSynopsisActions = isCompleted && !!prescriptionId;

  const accordionSx = {
    border: '1px solid rgba(0,0,0,0.09)', borderRadius: '10px !important',
    mb: 1, '&:before': { display: 'none' }, boxShadow: 'none',
  };
  const accordionSummarySx = {
    minHeight: 42, borderRadius: '10px !important',
    '& .MuiAccordionSummary-content': { my: 0 },
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
  };

  const renderResults = () => (
    <Box>
      {/* Done banner */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5,
        p: 1.75, borderRadius: 2.5,
        background: `linear-gradient(135deg, rgba(0,95,112,0.07) 0%, rgba(0,180,204,0.05) 100%)`,
        border: `1px solid rgba(0,121,142,0.2)`,
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `linear-gradient(135deg, ${TEAL_DARK}, ${TEAL_LIGHT})`,
          boxShadow: `0 2px 8px rgba(0,121,142,0.35)`,
        }}>
          <CheckIcon sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: TEAL_DARK, fontSize: '0.87rem' }}>
            Extraction complete!
          </Typography>
          <Typography variant="caption" sx={{ color: '#666' }}>
            Review and edit the pre-filled fields below.
          </Typography>
        </Box>
        {jobData?.ai_token_usage && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Chip size="small" label={jobData.ai_model_used} sx={{ fontSize: '0.6rem', height: 18, backgroundColor: 'rgba(0,121,142,0.1)', color: TEAL_DARK }} />
            <Chip size="small" label={`${jobData.ai_token_usage.total_tokens} tokens`} sx={{ fontSize: '0.6rem', height: 18, backgroundColor: 'rgba(0,121,142,0.1)', color: TEAL_DARK }} />
          </Box>
        )}
      </Box>

      {!aiResult && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem', borderRadius: 2 }}>
          Extraction completed but no data returned. Wait a moment, then re-open.
        </Alert>
      )}

      {/* PRIMARY: Synopsis & Follow-up editable form */}
      <Paper variant="outlined" sx={{
        p: 2, mb: 2, borderRadius: 2.5,
        borderColor: 'rgba(0,121,142,0.25)',
        backgroundColor: 'rgba(0,121,142,0.02)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.75 }}>
          <SynopsisIcon sx={{ fontSize: 18, color: TEAL }} />
          <Typography variant="subtitle2" sx={{
            fontWeight: 700, color: TEAL_DARK, fontSize: '0.82rem',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Synopsis & Follow-up
          </Typography>
        </Box>
        {renderSynopsisSection()}
      </Paper>

      {/* SECONDARY: Collapsible data sections */}
      <Accordion disableGutters sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={accordionSummarySx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon sx={{ fontSize: 16, color: TEAL }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: '#333', fontSize: '0.78rem' }}>
              Patient Info & Vitals
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
          {renderPatientInfo()}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={accordionSummarySx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#333', fontSize: '0.78rem' }}>
              Medications
            </Typography>
            {medCount > 0 && (
              <Chip size="small" label={medCount} sx={{ height: 18, fontSize: '0.62rem', backgroundColor: TEAL, color: '#fff', fontWeight: 700 }} />
            )}
            <FormControlLabel
              control={<Checkbox size="small" checked={applySynopsis} onChange={e => setApplySynopsis(e.target.checked)} sx={{ p: 0.25, '&.Mui-checked': { color: TEAL } }} />}
              label={<Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#888' }}>Include synopsis in Apply</Typography>}
              sx={{ m: 0, ml: 1 }}
              onClick={e => e.stopPropagation()}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {renderMedications()}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={accordionSummarySx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#333', fontSize: '0.78rem' }}>
              Investigations
            </Typography>
            {invCount > 0 && (
              <Chip size="small" label={invCount} sx={{ height: 18, fontSize: '0.62rem', backgroundColor: TEAL, color: '#fff', fontWeight: 700 }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {renderInvestigations()}
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER: Main Dialog
  // ────────────────────────────────────────────────────────────────────────────
  const selectedMedCount = Object.values(selectedMeds).filter(Boolean).length;
  const selectedInvCount = Object.values(selectedInvs).filter(Boolean).length;

  return (
    <Dialog
      open={open}
      onClose={() => { resetState(); onClose(); }}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}
    >
      {/* Teal gradient header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${TEAL_DARK} 0%, ${TEAL} 60%, ${TEAL_LIGHT} 100%)`,
        px: 3, py: 1.75,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScannerIcon sx={{ fontSize: 22, color: '#fff !important' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff !important', letterSpacing: '0.02em' }}>
            AI Prescription Scan
          </Typography>
          {prescriptionCode && (
            <Chip
              label={prescriptionCode} size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff !important', fontSize: '0.67rem', height: 20, fontWeight: 600 }}
            />
          )}
          {files.length > 1 && !jobRef && (
            <Chip
              label={`${files.length} files`} size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff !important', fontSize: '0.65rem', height: 20 }}
            />
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => { resetState(); onClose(); }}
          sx={{ color: '#fff !important', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5, overflowY: 'auto' }}>
        {/* Confidence legend — only when showing data */}
        {isCompleted && aiResult && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', backgroundColor: 'rgba(0,121,142,0.05)', borderRadius: 1.5, px: 1.5, py: 0.75 }}>
            <InfoIcon sx={{ fontSize: 15, color: TEAL, flexShrink: 0 }} />
            <Typography variant="caption" color="#555" sx={{ fontSize: '0.75rem' }}>
              Fields below <strong style={{ color: TEAL }}>{Math.round(threshold * 100)}%</strong> confidence are highlighted — review before applying.
            </Typography>
          </Box>
        )}

        {/* Phase 1: Upload */}
        {!jobRef && renderUploadZone()}

        {/* Phase 2: Processing (includes failed state with retry) */}
        {jobRef && !isCompleted && (
          <Box>
            {renderAnimatedStepper()}
            {isFailed && (
              <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                <Button startIcon={<RefreshIcon />} onClick={resetState} variant="outlined"
                  sx={{ borderColor: TEAL, color: TEAL, fontSize: '0.8rem', borderRadius: 2, px: 3 }}>
                  Try Again
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Phase 3: Completed — consolidated results */}
        {jobRef && isCompleted && renderResults()}
      </DialogContent>

      {/* Dialog actions */}
      <DialogActions sx={{
        px: 3, py: 1.5, gap: 1, flexWrap: 'wrap',
        borderTop: `1px solid rgba(0,121,142,0.1)`,
        backgroundColor: '#fafafa',
      }}>
        {/* Upload phase: Scan button */}
        {!jobRef && (
          <Button
            onClick={handleUpload}
            disabled={files.length === 0}
            variant="contained"
            startIcon={<AIIcon />}
            sx={{
              background: files.length > 0
                ? `linear-gradient(135deg, ${TEAL_DARK}, ${TEAL})`
                : undefined,
              color: '#fff', fontWeight: 700, borderRadius: 2, fontSize: '0.82rem', px: 2.5,
              boxShadow: files.length > 0 ? `0 2px 8px rgba(0,121,142,0.4)` : undefined,
              '&:hover': { background: `linear-gradient(135deg, ${TEAL_DARK}, ${TEAL_LIGHT})` },
            }}
          >
            Scan Prescription{files.length > 1 ? ` (${files.length} files)` : ''}
          </Button>
        )}

        {/* Completed phase: Save Synopsis + Create Follow-up */}
        {showSynopsisActions && (
          <>
            <Button
              onClick={handleUpdateSynopsis}
              disabled={synopsisSaving}
              variant="outlined"
              startIcon={synopsisSaving ? <CircularProgress size={14} sx={{ color: TEAL }} /> : <SaveIcon />}
              sx={{ borderColor: TEAL, color: TEAL, fontSize: '0.78rem', borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0,121,142,0.06)', borderColor: TEAL_DARK } }}
            >
              {synopsisSaving ? 'Saving…' : 'Save Synopsis'}
            </Button>
            <Button
              onClick={handleCreateFollowUp}
              disabled={followupSaving || !editFollowupDate}
              variant="outlined"
              startIcon={followupSaving ? <CircularProgress size={14} sx={{ color: '#7b1fa2' }} /> : <FollowUpIcon />}
              sx={{ borderColor: '#7b1fa2', color: '#7b1fa2', fontSize: '0.78rem', borderRadius: 2, '&:hover': { backgroundColor: 'rgba(123,31,162,0.06)', borderColor: '#6a1b9a' } }}
            >
              {followupSaving ? 'Creating…' : 'Create Follow-Up'}
            </Button>
          </>
        )}

        {/* Apply to form button (green) */}
        {isCompleted && aiResult && (
          <Button
            onClick={handleApply}
            variant="contained"
            startIcon={<AutoFillIcon />}
            sx={{
              background: 'linear-gradient(135deg, #2e7d32, #43a047)',
              color: '#fff', fontWeight: 700, fontSize: '0.78rem', borderRadius: 2,
              boxShadow: '0 2px 6px rgba(46,125,50,0.35)',
              '&:hover': { background: 'linear-gradient(135deg, #1b5e20, #388e3c)' },
            }}
          >
            Apply to Form
            {(selectedMedCount > 0 || selectedInvCount > 0) && ` (${selectedMedCount + selectedInvCount})`}
          </Button>
        )}

        <Button
          onClick={() => { resetState(); onClose(); }}
          variant="text"
          sx={{ color: '#888', fontSize: '0.78rem', ml: 'auto' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
